import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  Card as AntCard,
  Form as AntForm,
  Col,
  Input,
  Row,
  message,
  Button,
} from "antd";
import { api, Status } from "@api";
import { localStorage } from "@utils";
import { saveToken, updateIsNewUser } from "@store/auth/authSlice";
import { PRIVATE_ROUTE } from "../../route.constants";
import { IMG_AUTH_BG } from "@assets";
import { CheckOutlined } from "@ant-design/icons";
import ForgotPasswordModal from "./ForgotPasswordModal/ForgotPasswordModal";
import SuccessModal from "./SuccessModal/SuccessModal";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";

const { HOME, DASHBOARD, BUSINESS_INFO } = PRIVATE_ROUTE;

const RootContainer = styled.div`
  height: 100vh;
  overflow: auto;
  background-image: url(${IMG_AUTH_BG});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const Container = styled.div`
  width: 100%;
  max-width: 600px;
  margin: auto;
  display: flex;
  gap: 32px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 0rem;

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const TitleBox = styled.div`
  padding: 20px;
  max-width: 500px;
  border: 1px dashed ${(props) => props.theme.primaryColor};
  color: ${(props) => props.theme.primaryColor};
  font-size: 24px;
  font-weight: 700;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const Text = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #1d1014;
  text-align: center;
  width: 100%;
  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const Card = styled(AntCard)`
  width: 100%;
  max-width: 500px;
  border-radius: 24px !important;

  .ant-card-body {
    padding: 0 !important;
  }
`;

const Title = styled.div`
  padding: 24px 0;
  background: ${(props) => props.theme.backgroundColor};
  color: ${(props) => props.theme.primaryColor};
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  border-top-right-radius: 24px;
  border-top-left-radius: 24px;
`;

const SubTitle = styled(Text)`
  padding-top: 24px;
  text-align: center;
`;

const Form = styled(AntForm)`
  padding: 24px;

  .ant-form-item:first-child {
    margin-bottom: 20px !important;
  }

  .ant-btn {
    width: 100%;
    height: 56px;
    padding: 12px 16px;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
  }

  .ant-btn-icon {
    display: flex;
  }
`;

const LinkText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${(props) => props.theme.primaryColor};
  cursor: pointer;
  text-align: right;
  line-height: 1.5;
`;

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.token);
  const [apiStatus, setApiStatus] = useState(Status.DEFAULT);
  const [open, setOpen] = useState(false);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [browser, setBrowser] = useState("chrome");
  const [admin, setAdmin] = useState();
  const [storeLang, setStoreLang] = useState();

  const {t} = useTranslation();

  useEffect(() => {
    getCheckSubDomain();
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes("firefox")) setBrowser("firefox");
    else if (userAgent.includes("edg")) setBrowser("edge");
    else if (userAgent.includes("chrome")) setBrowser("chrome");
    else if (userAgent.includes("safari")) setBrowser("safari");
    else setBrowser("unknown");
  }, []);

  const getCheckSubDomain = () => {
    api
      .getCheckSubDomain()
      .then(({ data }) => {
        setAdmin(data?.admin);
        setStoreLang(data?.storeLanguages)
      })
      .catch((err) => {
        console.error("Failed to fetch subdomain info", err);
      });
  };


  const onFinish = ({ username, password }) => {
    if (apiStatus !== Status.LOADING) {
      setApiStatus(Status.LOADING);
      api
        .login({
          username,
          password,
          device: uuid(),
          browser: browser,
        })
        .then((response) => {
          const {
            data: { access_token, admin, first_time_login },
          } = response;
          setApiStatus(Status.SUCCESS);
          message.success(t('login_success'));
          dispatch(saveToken(access_token));
          dispatch(updateIsNewUser(first_time_login));
          localStorage.setToken(access_token);
          localStorage.setAdmin(JSON.stringify(admin));
          localStorage.setStoreLang(storeLang);
          api.setSessionToken(access_token);
        })
        .catch((error) => {
          setApiStatus(Status.ERROR);
          message.error(error);
        });
    }
  };

  const successCallback = () => {
    setOpen(false);
    setOpenSuccess(true);
  };

  if (isLoggedIn) {
    return <Navigate to={`/${HOME}/${DASHBOARD}`} />;
  }

  return (
    <RootContainer>
      <Row gutter={[0, 0]} style={{ margin: 0, justifyContent: "center" }}>
        <Col xs={22} sm={20} md={16} lg={10}>
          <Container>
            <TitleBox>{t('login_title')}</TitleBox>
            <Text>
             {t('login_desc')}
            </Text>
            <Card>
              <Title>{t('login_subtext')}</Title>
              <SubTitle>
                {t('login_sub')}
              </SubTitle>
              <Form layout="vertical" requiredMark={false} onFinish={onFinish}>
                <Form.Item name="username">
                  <Input size="large" placeholder={t('username')} />
                </Form.Item>
                <Form.Item name="password">
                  <Input.Password size="large" placeholder={t('password')} />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={apiStatus === Status.LOADING}
                    icon={<CheckOutlined />}
                  >
                    {t('submit')}
                  </Button>
                </Form.Item>
                <LinkText onClick={() => setOpen(true)}>
                  {t('forgot_pass')}
                </LinkText>
              </Form>
            </Card>
          </Container>
        </Col>
      </Row>
      <ForgotPasswordModal
        open={open}
        admin={admin}
        handleClose={() => setOpen(false)}
        successCallback={successCallback}
      />
      <SuccessModal
        open={openSuccess}
        handleClose={() => setOpenSuccess(false)}
      />
    </RootContainer>
  );
};

export default Login;
